import { Btn, CartState, HEROES, heroDef, type HeroId, MAPS, Phase, Slot, wrapAngle } from "@rivalwatch/shared";
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast as toastCue } from "../audio/cues/ui.ts";
import { playUi } from "../audio/ui.ts";
import { AbilityIcon, RoleIcon } from "./icons.tsx";
import { HeroPortrait } from "./Portrait.tsx";
import { type AbilityHud, type ConvoyHud, type HudState, type MatchHud, type RosterEntry, ui } from "./store.ts";

/**
 * Combat HUD, styled after Marvel Rivals: team rosters flanking the objective up top,
 * slanted comic panels, hero portraits, resource arcs at the crosshair, ability tiles and
 * an ultimate emblem at the bottom.
 */
export function Hud() {
  const h = ui.hud.value;
  const cap = h ? h.maxHp + h.maxArmor + h.maxShield : 1;
  const low = !!h && h.alive && (h.hp + h.armor + h.shield) / cap < 0.35;
  useDeathDesaturate(!!h && !h.alive);
  return (
    <div class="hud rv">
      <Vignettes low={low} />
      {h?.match && <TopHud m={h.match} roster={h.roster} />}
      <KillFeed />
      {h?.match && <PhaseBanner m={h.match} />}
      {h?.alive && <Reticle h={h} />}
      {h?.alive && <DamageDirections h={h} />}
      {h?.alive && h.targeting && (
        <div class="rv-aim-hint" role="status">
          <b>{h.targeting.name}</b>
          <span>
            <kbd>LMB</kbd>
            {h.targeting.verb}
          </span>
          {h.targeting.cancel && (
            <span>
              <kbd>RMB</kbd>Cancel
            </span>
          )}
        </div>
      )}
      <KoPrompts />
      {h && h.alive && h.status.length > 0 && <Statuses list={h.status} />}
      {h && !h.alive && <DeathCard h={h} />}
      {h && <Vitals h={h} low={low} />}
      {h && <Kit h={h} />}
      {h && <UltEmblem h={h} />}
      {h?.inSpawn && h.alive && (
        <div class="rv-prompt">
          <kbd>H</kbd>Change hero
        </div>
      )}
      <Toast />
      {!ui.locked.value && ui.overlay.value === "none" && !ui.chatOpen.value && (
        <div class="rv-clicktoplay">
          <span>Click to play</span>
        </div>
      )}
      {ui.scoped.value && <div class="scope" aria-hidden="true" />}
      {ui.netGraph.value && <NetGraph />}
    </div>
  );
}

/** Re-renders every `ms` so time-based fades (feed, banners) expire without new data. */
function useTicker(ms: number) {
  const [, tick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => tick((n) => n + 1), ms);
    return () => clearInterval(iv);
  }, [ms]);
}

function useDeathDesaturate(dead: boolean) {
  useEffect(() => {
    document.querySelector("canvas.world")?.classList.toggle("dead", dead);
  }, [dead]);
}

// ------------------------------------------------------------------ top: rosters + objective

function TopHud({ m, roster }: { m: MatchHud; roster: RosterEntry[] }) {
  const six = (list: (RosterEntry | undefined)[]) => [...list, ...Array.from({ length: 6 }, () => undefined)].slice(0, 6);
  const allies = six(roster.filter((r) => r.friend));
  const foes = six(roster.filter((r) => !r.friend));
  return (
    <div class="rv-top">
      <div class="rv-roster ally">
        {allies.map((r, i) => (
          <RosterTile key={r?.id ?? `a${i}`} r={r} side="ally" />
        ))}
      </div>
      {m.convoy ? <ConvoyObjective m={m} c={m.convoy} /> : <Objective m={m} />}
      <div class="rv-roster foe">
        {foes.map((r, i) => (
          <RosterTile key={r?.id ?? `f${i}`} r={r} side="foe" />
        ))}
      </div>
    </div>
  );
}

function RosterTile({ r, side }: { r?: RosterEntry; side: "ally" | "foe" }) {
  if (!r) return <div class={`rv-tile empty ${side}`} aria-hidden="true" />;
  const ultReady = side === "ally" && r.ult >= 100;
  return (
    <div
      class={`rv-tile ${side}${r.alive ? "" : " dead"}${r.me ? " me" : ""}${ultReady ? " ult" : ""}`}
      title={`${r.name} — ${HEROES[r.hero].name}${r.alive ? "" : " (dead)"}`}
    >
      <div class="rv-tile-frame">
        <HeroPortrait hero={r.hero} />
        <span class="rv-tile-role">
          <RoleIcon role={HEROES[r.hero].role} size={12} />
        </span>
        {!r.alive && <span class="rv-tile-timer">{r.respawn > 0 ? r.respawn : ""}</span>}
      </div>
      {side === "ally" && (
        <span class="rv-tile-ult" aria-hidden="true">
          <i style={{ width: `${Math.min(100, r.ult)}%` }} />
        </span>
      )}
    </div>
  );
}

const clock = (sec: number) => `${Math.floor(Math.max(0, sec) / 60)}:${String(Math.max(0, sec) % 60).padStart(2, "0")}`;

function objectiveState(m: MatchHud): { text: string; tone: string } {
  if (m.convoy) return convoyState(m, m.convoy);
  switch (m.phase) {
    case Phase.Warmup:
      return { text: "Warmup · host starts the match", tone: "" };
    case Phase.Setup:
      return { text: `Point unlocks in 0:${String(m.phaseSec).padStart(2, "0")}`, tone: "" };
    case Phase.InProgress:
      if (m.overtime) return { text: "Overtime", tone: "hot" };
      if (m.contested) return { text: "Contested", tone: "hot" };
      if (m.capTeam !== "none" && m.capture > 0) return m.capTeam === "friend" ? { text: "Capturing", tone: "ally" } : { text: "Enemy capturing", tone: "foe" };
      if (m.owner === "none") return { text: "Capture the point", tone: "" };
      return m.owner === "friend" ? { text: "Defend the point", tone: "ally" } : { text: "Retake the point", tone: "foe" };
    case Phase.RoundEnd:
      return m.winner === "friend" ? { text: "Round won", tone: "ally" } : { text: "Round lost", tone: "foe" };
    case Phase.MatchEnd:
      return m.winner === "none" ? { text: "Draw", tone: "" } : m.winner === "friend" ? { text: "Victory", tone: "ally" } : { text: "Defeat", tone: "foe" };
  }
}

function convoyState(m: MatchHud, c: ConvoyHud): { text: string; tone: string } {
  switch (m.phase) {
    case Phase.Warmup:
      return { text: "Warmup · host starts the match", tone: "" };
    case Phase.Setup:
      return c.attacking ? { text: `Gates open in ${clock(m.phaseSec)}`, tone: "" } : { text: `Set up the defence · ${clock(m.phaseSec)}`, tone: "" };
    case Phase.InProgress:
      if (m.overtime) return { text: "Overtime", tone: "hot" };
      if (c.state === CartState.Contested) return { text: "Contested", tone: "hot" };
      if (c.state === CartState.Rolling) return { text: "Payload rolling back", tone: c.attacking ? "foe" : "ally" };
      if (c.state === CartState.Pushing) return c.attacking ? { text: "Escorting the payload", tone: "ally" } : { text: "Stop the payload!", tone: "foe" };
      if (c.state === CartState.Blocked) return c.attacking ? { text: "Clear the payload", tone: "foe" } : { text: "Payload held", tone: "ally" };
      return c.attacking ? { text: "Escort the payload", tone: "" } : { text: "Hold the line", tone: "" };
    case Phase.RoundEnd:
      if (m.round <= 1) return c.cart >= 1 ? { text: "Payload delivered", tone: c.attacking ? "ally" : "foe" } : { text: "Switching sides", tone: "" };
      return m.winner === "none" ? { text: "Draw", tone: "" } : m.winner === "friend" ? { text: "We win", tone: "ally" } : { text: "They win", tone: "foe" };
    case Phase.MatchEnd:
      return m.winner === "none" ? { text: "Draw", tone: "" } : m.winner === "friend" ? { text: "Victory", tone: "ally" } : { text: "Defeat", tone: "foe" };
  }
}

/** What round two has to beat (or hold), in words. */
function goalText(c: ConvoyHud): string | undefined {
  const g = c.goal;
  if (!g) return undefined;
  const pct = `${Math.floor(g.frac * 100)}%`;
  if (c.attacking) return g.delivered ? `Deliver with over ${clock(g.leftSec)} left` : `Push past ${pct}`;
  return g.delivered ? `They need over ${clock(g.leftSec)} left` : `Hold them under ${pct}`;
}

/**
 * Convoy: role and attack clock over the route — checkpoints, the payload, and in round two the
 * first attack's mark — then who's on the payload and what it's doing.
 */
function ConvoyObjective({ m, c }: { m: MatchHud; c: ConvoyHud }) {
  const { text, tone } = objectiveState(m);
  const warm = m.phase === Phase.Warmup;
  const side = c.attacking ? "ally" : "foe";
  const time = m.phase === Phase.Setup ? m.phaseSec : c.clockSec;
  const goal = goalText(c);
  const pct = Math.floor(c.cart * 100);
  return (
    <div
      class={`rv-convoy${m.overtime ? " overtime" : ""}`}
      role="status"
      aria-label={`Convoy, round ${m.round}. ${warm ? "Warmup" : c.attacking ? "Attacking" : "Defending"}. Payload ${pct} percent along. ${text}${goal ? `. ${goal}` : ""}`}
    >
      <div class="rv-convoy-head">
        <span class={`rv-convoy-role ${warm ? "" : side}`}>{warm ? "Warmup" : c.attacking ? "Attack" : "Defend"}</span>
        <b class={`rv-convoy-clock${m.overtime ? " hot" : ""}`}>{warm ? "–:––" : m.overtime ? "OT" : clock(time)}</b>
        <span class="rv-convoy-round">{warm ? "Convoy" : `Round ${Math.max(1, m.round)} of 2`}</span>
      </div>
      <div class={`rv-convoy-track ${side}`} aria-hidden="true">
        <i class="rv-convoy-fill" style={{ width: `${c.cart * 100}%` }} />
        {c.checkpoints.map((f, i) => (
          <i key={i} class={`rv-convoy-cp${i < c.passed ? " on" : ""}`} style={{ left: `${f * 100}%` }} />
        ))}
        {c.goal && <i class={`rv-convoy-goal ${c.goal.ours ? "ally" : "foe"}`} style={{ left: `${c.goal.frac * 100}%` }} />}
        <i class="rv-convoy-end" />
        <i class={`rv-convoy-cart s${c.state}`} style={{ left: `${c.cart * 100}%` }} />
      </div>
      <div class="rv-convoy-foot">
        <span class="rv-obj-onpoint" aria-hidden="true">
          {c.pushers[0] > 0 && <span class="ally">{c.pushers[0]}</span>}
          {c.pushers[1] > 0 && <span class="foe">{c.pushers[1]}</span>}
        </span>
        <span class={`rv-state ${tone}`}>{text}</span>
        <span class="rv-convoy-pct">{pct}%</span>
      </div>
      {goal && <div class="rv-convoy-goalnote">{goal}</div>}
    </div>
  );
}

function Objective({ m }: { m: MatchHud }) {
  const toWin = ui.lobby.value?.settings.match.roundsToWin ?? 2;
  const { text, tone } = objectiveState(m);
  const pct = (v: number) => Math.floor(v);
  return (
    <div class={`rv-obj${m.overtime ? " overtime" : ""}`} role="status" aria-label={`Round ${m.round}. You ${pct(m.progress[0])} percent, enemy ${pct(m.progress[1])} percent. ${text}`}>
      <div class={`rv-pct ally${m.owner === "friend" ? " holding" : ""}`}>
        <b>{pct(m.progress[0])}</b>
        <i>%</i>
      </div>
      <PointEmblem m={m} />
      <div class={`rv-pct foe${m.owner === "foe" ? " holding" : ""}`}>
        <b>{pct(m.progress[1])}</b>
        <i>%</i>
      </div>
      <div class="rv-obj-bar ally" aria-hidden="true">
        <i style={{ width: `${m.progress[0]}%` }} />
      </div>
      <div class="rv-obj-onpoint" aria-hidden="true">
        {m.onPoint[0] > 0 && <span class="ally">{m.onPoint[0]}</span>}
        {m.onPoint[1] > 0 && <span class="foe">{m.onPoint[1]}</span>}
      </div>
      <div class="rv-obj-bar foe" aria-hidden="true">
        <i style={{ width: `${m.progress[1]}%` }} />
      </div>
      <div class="rv-rounds ally" aria-hidden="true">
        {Array.from({ length: toWin }, (_, i) => (
          <i class={m.score[0] > i ? "on" : ""} />
        ))}
      </div>
      <div class={`rv-state ${tone}`}>{text}</div>
      <div class="rv-rounds foe" aria-hidden="true">
        {Array.from({ length: toWin }, (_, i) => (
          <i class={m.score[1] > i ? "on" : ""} />
        ))}
      </div>
    </div>
  );
}

const hexPoints = (r: number, cx = 40, cy = 40) =>
  Array.from({ length: 6 }, (_, k) => {
    const a = ((-90 + 60 * k) * Math.PI) / 180;
    return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
  }).join(" ");

function PointEmblem({ m }: { m: MatchHud }) {
  const fill = m.owner === "friend" ? "var(--rv-blue)" : m.owner === "foe" ? "var(--rv-red)" : "var(--rv-ink-solid)";
  const capColor = m.capTeam === "friend" ? "var(--rv-blue)" : "var(--rv-red)";
  const locked = m.phase === Phase.Setup || m.phase === Phase.Warmup;
  return (
    <svg class={`rv-point${m.contested ? " contested" : ""}`} viewBox="0 0 80 80" aria-hidden="true">
      <polygon points={hexPoints(36)} class="rv-point-halo" />
      <polygon points={hexPoints(29)} fill={fill} stroke="var(--rv-white)" stroke-width="2.5" />
      <polygon points={hexPoints(29)} fill="url(#rv-halftone)" opacity="0.5" />
      {m.capture > 0 && m.capTeam !== "none" && (
        <polygon points={hexPoints(34)} fill="none" stroke={capColor} stroke-width="5" pathLength={100} stroke-dasharray={`${m.capture * 100} 100`} class="rv-point-cap" />
      )}
      {m.contested && <polygon points={hexPoints(34)} fill="none" stroke="var(--rv-gold)" stroke-width="3" pathLength={100} stroke-dasharray="6 4" class="rv-point-contest" />}
      {locked ? (
        m.phase === Phase.Setup ? (
          <text x="40" y="49" class="rv-point-glyph small">
            {m.phaseSec}
          </text>
        ) : (
          <path d="M33 38v-5a7 7 0 0 1 14 0v5M30 38h20v14H30z" fill="none" stroke="var(--rv-white)" stroke-width="3" stroke-linejoin="round" />
        )
      ) : (
        <text x="40" y="51" class="rv-point-glyph">
          A
        </text>
      )}
      <defs>
        <pattern id="rv-halftone" width="4" height="4" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="0.9" fill="#000" opacity="0.45" />
        </pattern>
      </defs>
    </svg>
  );
}

/** Big comic splash on key phase changes: point unlocked, round won/lost. */
function PhaseBanner({ m }: { m: MatchHud }) {
  const prev = useRef(m.phase);
  const [banner, setBanner] = useState<{ text: string; sub: string; tone: string; at: number } | undefined>();
  useTicker(250);
  useEffect(() => {
    const p = prev.current;
    prev.current = m.phase;
    if (p === m.phase) return;
    const c = m.convoy;
    if (c) {
      if (m.phase === Phase.InProgress && p === Phase.Setup) setBanner({ text: "Payload unlocked", sub: c.attacking ? "Escort it to the depot" : "Stop it reaching the depot", tone: "gold", at: Date.now() });
      else if (m.phase === Phase.Setup) setBanner({ text: `Round ${m.round}`, sub: c.attacking ? "Attack" : "Defend", tone: c.attacking ? "ally" : "foe", at: Date.now() });
      else if (m.phase === Phase.RoundEnd) {
        const { text, tone } = convoyState(m, c);
        setBanner({ text, sub: `Payload ${Math.floor(c.cart * 100)}% along`, tone: tone || "gold", at: Date.now() });
      }
      return;
    }
    if (m.phase === Phase.InProgress && p === Phase.Setup) setBanner({ text: "Point unlocked", sub: m.stageName ?? `Round ${m.round}`, tone: "gold", at: Date.now() });
    else if (m.phase === Phase.Setup) setBanner({ text: `Round ${m.round}`, sub: m.stageName ?? "Get ready", tone: "gold", at: Date.now() });
    else if (m.phase === Phase.RoundEnd) setBanner({ text: m.winner === "friend" ? "Round won" : "Round lost", sub: `${m.score[0]} – ${m.score[1]}`, tone: m.winner === "friend" ? "ally" : "foe", at: Date.now() });
  }, [m.phase]);
  // Convoy: a checkpoint falling is worth a splash of its own.
  const passed = useRef(m.convoy?.passed ?? 0);
  useEffect(() => {
    const n = m.convoy?.passed ?? 0;
    const before = passed.current;
    passed.current = n;
    if (!m.convoy || n <= before || m.phase !== Phase.InProgress) return;
    setBanner(
      m.convoy.attacking
        ? { text: "Checkpoint reached", sub: "Extra time · spawn moved up", tone: "ally", at: Date.now() }
        : { text: "Checkpoint lost", sub: "Fall back to the next one", tone: "foe", at: Date.now() },
    );
  }, [m.convoy?.passed]);
  if (!banner || Date.now() - banner.at > 2600) return null;
  return (
    <div key={banner.at} class={`rv-banner ${banner.tone}`} role="status">
      <b>{banner.text}</b>
      <span>{banner.sub}</span>
    </div>
  );
}

// ------------------------------------------------------------------ kill feed & eliminations

function KillFeed() {
  useTicker(500);
  const now = Date.now();
  return (
    <div class="rv-feed" aria-live="polite">
      {ui.feed.value
        .filter((k) => now - k.at < 6500)
        .map((k) => (
          <div key={k.id} class={`rv-kill${k.mine ? " mine" : ""}`}>
            {k.killer ? (
              <span class={`rv-kill-side ${k.killerFriend ? "ally" : "foe"}`}>
                <HeroPortrait hero={k.killerHero as 0} />
                <span class="rv-kill-name">{k.killer}</span>
                {k.helpers.length > 0 && (
                  <span class="rv-kill-helpers" aria-label={`with ${k.helpers.map((x) => x.name).join(", ")}`}>
                    {k.helpers.slice(0, 3).map((x) => (
                      <HeroPortrait key={x.name} hero={x.hero as 0} class={x.kind} title={`${x.name} · ${x.kind === "ko" ? "KO" : "assist"}`} />
                    ))}
                    {k.helpers.length > 3 && <span class="rv-kill-more">+{k.helpers.length - 3}</span>}
                  </span>
                )}
              </span>
            ) : (
              <span class="rv-kill-side world">
                <span class="rv-kill-name">{MAPS[ui.lobby.value?.map ?? "foundry"].title}</span>
              </span>
            )}
            <svg class="rv-kill-icon" viewBox="0 0 24 24" aria-label="eliminated">
              <path d="M3 5l9 7-9 7M12 5l9 7-9 7" />
            </svg>
            <span class={`rv-kill-side victim ${k.victimFriend ? "ally" : "foe"}`}>
              <span class="rv-kill-name">{k.victim}</span>
              <HeroPortrait hero={k.victimHero as 0} />
            </span>
          </div>
        ))}
    </div>
  );
}

const MULTI = ["", "", "Double", "Triple", "Quad", "Penta", "Hexa"];

/** Two hands clasped: the Rivals assist mark. A filled silhouette so it reads at 2em. */
const Handshake = () => (
  <svg class="rv-assist-icon" viewBox="0 0 32 32" aria-hidden="true">
    <path d="M1 11.5 6.5 8 9.8 13.3 4.3 16.8ZM31 11.5 25.5 8 22.2 13.3 27.7 16.8Z" />
    <path d="M8 9.5 13 7.2 16 9.2 19 7.2 24 9.5 23.5 15.2 18.6 20.6 15.2 22.6 12.2 21.6 10 19.2 8.2 16.6Z" />
    <path class="crease" d="M12.2 14.6 15.2 17.6M14.2 12.6 17.2 15.6M16.4 10.8 19.2 13.6M16 9.2 13.2 12" />
  </svg>
);

/**
 * KOs and assists you just earned, stacked beside the crosshair where Rivals puts its KO prompt:
 * a stamped "KO" over the victim's portrait, slashed through in red ink. Final hits are gold,
 * shared KOs name the teammate who finished it, assists get the handshake.
 */
function KoPrompts() {
  useTicker(200);
  const now = Date.now();
  const all = ui.kos.value;
  const shown = all.filter((n) => now - n.at < (n.kind === "assist" ? 2400 : 2900)).slice(-3);
  if (!shown.length) return null;
  // Multi-KO: KOs (not assists) chained within 4 s of each other.
  const ko = all.filter((n) => n.kind !== "assist");
  let multi = ko.length ? 1 : 0;
  for (let i = ko.length - 1; i > 0 && ko[i]!.at - ko[i - 1]!.at < 4000; i--) multi++;
  const lastKo = ko.at(-1);
  const showMulti = multi >= 2 && lastKo && now - lastKo.at < 2400;
  return (
    <div class="rv-kos" aria-live="polite">
      {showMulti && (
        <div key={`m${lastKo.id}`} class="rv-multi">
          <b>{MULTI[Math.min(multi, 6)]}</b> KO
        </div>
      )}
      {shown
        .slice()
        .reverse()
        .map((n) =>
          n.kind === "assist" ? (
            <div key={n.id} class="rv-assist" role="status">
              <Handshake />
              <span class="rv-assist-text">
                <small>Assist</small>
                <b>{n.partner?.name ?? "Your team"}</b>
              </span>
              <span class="rv-assist-victim">
                <HeroPortrait hero={n.victimHero as 0} title={n.victim} />
                <i aria-hidden="true" />
              </span>
            </div>
          ) : (
            <div key={n.id} class={`rv-ko ${n.kind}`} role="status">
              <span class="rv-ko-card">
                <HeroPortrait hero={n.victimHero as 0} />
                <i class="rv-ko-slash" aria-hidden="true" />
              </span>
              <span class="rv-ko-stamp" aria-hidden="true">
                KO
              </span>
              <span class="rv-ko-text">
                <small>{n.kind === "final" ? "Final hit" : `with ${n.partner?.name ?? "your team"}`}</small>
                <b>{n.victim}</b>
              </span>
            </div>
          ),
        )}
    </div>
  );
}

// ------------------------------------------------------------------ crosshair & resource arcs

const RES_COLOR: Record<string, string> = {
  "Jet fuel": "#7fd6ff",
  "Rocket fuel": "#7fd6ff",
  Ward: "#ffd23f",
  "Dark magic": "#c08bff",
  Levitate: "#9ff3ff",
  Breath: "#5cffa1",
};

const polar = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180;
  return `${(100 + r * Math.cos(a)).toFixed(2)} ${(100 - r * Math.sin(a)).toFixed(2)}`;
};
const arc = (from: number, to: number, r: number, sweep: 0 | 1) => `M ${polar(from, r)} A ${r} ${r} 0 0 ${sweep} ${polar(to, r)}`;

function ResArc({ slot, label, value }: { slot: 0 | 1 | 2; label: string; value: number }) {
  // Left arc fills bottom→top, right arc bottom→top, bottom arc left→right.
  const geo = [
    { from: 215, span: -70, sweep: 1 as const, lx: 18, ly: 150, anchor: "start" },
    { from: -35, span: 70, sweep: 0 as const, lx: 182, ly: 150, anchor: "end" },
    { from: 235, span: 70, sweep: 0 as const, lx: 100, ly: 190, anchor: "middle" },
  ][slot]!;
  const r = slot === 2 ? 58 : 66;
  const v = Math.max(0, Math.min(1, value));
  const color = RES_COLOR[label] ?? "#f5f7ff";
  return (
    <g class={`rv-arc${v < 0.25 ? " low" : ""}`} style={{ "--arc": color }}>
      <path d={arc(geo.from, geo.from + geo.span, r, geo.sweep)} class="rv-arc-bg" />
      {v > 0.001 && <path d={arc(geo.from, geo.from + geo.span * v, r, geo.sweep)} class="rv-arc-fg" />}
      <text x={geo.lx} y={geo.ly} text-anchor={geo.anchor} class="rv-arc-label">
        {label}
      </text>
    </g>
  );
}

function Reticle({ h }: { h: HudState }) {
  const hit = ui.hit.value;
  const res = h.resources.slice(0, 3);
  const reload = h.reload;
  const RR = 24;
  const rc = 2 * Math.PI * RR;
  return (
    <div class="rv-reticle" aria-hidden="true">
      <svg viewBox="0 0 200 200">
        {res.map((r, i) => (
          <ResArc key={r.label} slot={i as 0 | 1 | 2} label={r.label} value={r.value} />
        ))}
        {reload > 0 && (
          <g class="rv-reload">
            <circle cx="100" cy="100" r={RR} class="rv-reload-bg" />
            <circle cx="100" cy="100" r={RR} class="rv-reload-fg" stroke-dasharray={`${rc * reload} ${rc}`} transform="rotate(-90 100 100)" />
          </g>
        )}
        <g class="rv-cross">
          <path d="M100 88v-7M100 112v7M88 100h-7M112 100h7" class="rv-cross-ink" />
          <path d="M100 88v-7M100 112v7M88 100h-7M112 100h7" class="rv-cross-line" />
          <circle cx="100" cy="100" r="1.8" class="rv-cross-dot" />
        </g>
      </svg>
      {hit.at > 0 && performance.now() - hit.at < 450 && (
        <svg key={hit.at} viewBox="0 0 200 200" class={`rv-hit${hit.crit ? " crit" : ""}${hit.kill ? " kill" : ""}`}>
          <path d="M91 91l-8-8M109 91l8-8M91 109l-8 8M109 109l8 8" class="rv-hit-ink" />
          <path d="M91 91l-8-8M109 91l8-8M91 109l-8 8M109 109l8 8" class="rv-hit-line" />
          {hit.kill && <circle cx="100" cy="100" r="20" class="rv-hit-ring" />}
        </svg>
      )}
    </div>
  );
}

/** Red wedges around the crosshair pointing at whoever just hit you. */
function DamageDirections({ h }: { h: HudState }) {
  const now = performance.now();
  const dirs = ui.dmgDirs.value.filter((d) => now - d.at < 1100);
  if (!dirs.length) return null;
  return (
    <div class="rv-dirs" aria-hidden="true">
      {dirs.map((d) => {
        const dx = d.x - h.view.x;
        const dz = d.z - h.view.z;
        const toYaw = Math.atan2(-dx, -dz);
        const rel = wrapAngle(toYaw - h.view.yaw);
        const fade = 1 - (now - d.at) / 1100;
        return (
          <svg key={d.id} viewBox="-100 -100 200 200" style={{ transform: `translate(-50%, -50%) rotate(${-rel}rad)`, opacity: fade }}>
            <path d="M-26 -84 Q0 -94 26 -84 L14 -70 Q0 -76 -14 -70 Z" />
          </svg>
        );
      })}
    </div>
  );
}

function Statuses({ list }: { list: string[] }) {
  const good = new Set(["Damage boost", "Invulnerable", "Healing boosted", "Damage reduced"]);
  return (
    <div class="rv-statuses">
      {list.map((s) => (
        <span key={s} class={good.has(s) ? "good" : "bad"}>
          {s}
        </span>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ bottom-left: hero card + health

/** Value that trails behind drops (the red "chip" on a health bar); snaps up on heals. */
function useChip(v: number) {
  const [ghost, setGhost] = useState(v);
  const timer = useRef(0);
  useEffect(() => {
    if (v >= ghost) {
      clearTimeout(timer.current);
      setGhost(v);
      return;
    }
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setGhost(v), 420);
    return () => clearTimeout(timer.current);
  }, [v]);
  return ghost;
}

function Pool({ kind, value, max, width }: { kind: string; value: number; max: number; width: number }) {
  const f = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const ghost = useChip(f);
  return (
    <div class={`rv-pool ${kind}`} style={{ width: `${width}%` }}>
      <i class="rv-pool-ghost" style={{ width: `${Math.max(f, ghost) * 100}%` }} />
      <i class="rv-pool-fill" style={{ width: `${f * 100}%` }} />
    </div>
  );
}

function Vitals({ h, low }: { h: HudState; low: boolean }) {
  const def = heroDef(h.hero, h.form);
  const pools = [
    { kind: "hp", value: h.hp, max: h.maxHp },
    { kind: "armor", value: h.armor, max: h.maxArmor },
    { kind: "shield", value: h.shield, max: h.maxShield },
    { kind: "over", value: h.over, max: h.over },
  ].filter((p) => p.max > 0.5);
  const cap = pools.reduce((s, p) => s + p.max, 0);
  const total = Math.ceil(h.hp + h.armor + h.shield + h.over);
  const base = h.maxHp + h.maxArmor + h.maxShield;
  return (
    <div class={`rv-vitals${low ? " low" : ""}`}>
      <div class="rv-card" style={{ "--hc": def.color }}>
        <HeroPortrait hero={h.hero} />
        <span class="rv-card-name">{def.name}</span>
      </div>
      <div class="rv-hp">
        <div class="rv-hpnum" aria-label={`Health ${total} of ${base}`}>
          <b>{total}</b>
          <span>/{base}</span>
          {h.over > 0.5 && <em class="over">+{Math.ceil(h.over)}</em>}
        </div>
        <div class="rv-hpbar" style={{ "--notch": `${(25 / Math.max(cap, 25)) * 100}%` }}>
          {pools.map((p) => (
            <Pool key={p.kind} kind={p.kind} value={p.value} max={p.max} width={(p.max / cap) * 100} />
          ))}
        </div>
        <div class="rv-legend" aria-hidden="true">
          {h.maxArmor > 0 && <span class="armor">Armor</span>}
          {h.maxShield > 0 && <span class="shield">Shield</span>}
          {h.over > 0.5 && <span class="over">Bonus</span>}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ bottom-right: weapon + abilities

const SLOT_BTN: Record<number, number> = { [Slot.Secondary]: Btn.Secondary, [Slot.Ability1]: Btn.Ability1, [Slot.Ability2]: Btn.Ability2, [Slot.Ability3]: Btn.Ability3 };
const keyLabel = (k: string) => k.split(/[\s/(]/)[0]!.toUpperCase();

function AbilityTile({ a, held, hero }: { a: AbilityHud; held: boolean; hero: HeroId }) {
  const ready = !a.hasCooldown || a.charges > 0;
  const cooling = a.hasCooldown && a.charges === 0 && a.cdSec > 0;
  const prev = useRef(ready);
  const [fx, setFx] = useState<{ kind: "cast" | "ready"; at: number } | undefined>();
  useEffect(() => {
    if (prev.current && !ready) setFx({ kind: "cast", at: Date.now() });
    else if (!prev.current && ready) setFx({ kind: "ready", at: Date.now() });
    prev.current = ready;
  }, [ready]);
  return (
    <div class={`rv-ab ${ready ? "ready" : "cooling"}${held ? " held" : ""}`} title={`${a.name} (${a.key})`}>
      <div class="rv-ab-face">
        <span class="rv-ab-icon">
          <AbilityIcon name={a.name} hero={hero} slot={a.slot} size={30} />
        </span>
        {cooling && <span class="rv-ab-cd" style={{ height: `${Math.min(1, a.cdFrac) * 100}%` }} />}
        {cooling && <b class="rv-ab-num">{Math.ceil(a.cdSec)}</b>}
        {a.maxCharges > 1 && (
          <span class="rv-ab-charges">
            {Array.from({ length: a.maxCharges }, (_, i) => (
              <i class={i < a.charges ? "on" : ""} />
            ))}
          </span>
        )}
        {fx && <span key={fx.at} class={`rv-ab-fx ${fx.kind}`} />}
      </div>
      <kbd class="rv-ab-key">{keyLabel(a.key)}</kbd>
    </div>
  );
}

function AmmoTicks({ ammo, max, reload }: { ammo: number; max: number; reload: number }) {
  if (max > 40) {
    return (
      <div class="rv-ammobar">
        <i style={{ width: `${(reload > 0 ? reload : ammo / max) * 100}%` }} class={reload > 0 ? "reloading" : ""} />
      </div>
    );
  }
  const lit = reload > 0 ? Math.floor(reload * max) : ammo;
  return (
    <div class={`rv-ticks${reload > 0 ? " reloading" : ""}`} style={{ "--n": max }}>
      {Array.from({ length: max }, (_, i) => (
        <i class={i < lit ? "on" : ""} />
      ))}
    </div>
  );
}

function Kit({ h }: { h: HudState }) {
  const def = heroDef(h.hero, h.form);
  const lowAmmo = h.maxAmmo > 0 && h.reload === 0 && h.ammo <= Math.ceil(h.maxAmmo * 0.25);
  return (
    <div class="rv-kit">
      <div class={`rv-weapon${lowAmmo ? " low" : ""}`}>
        <span class="rv-weapon-name">{def.abilities[Slot.Primary].name}</span>
        {h.maxAmmo > 0 ? (
          <div class="rv-ammo" aria-label={`Ammo ${h.ammo} of ${h.maxAmmo}`}>
            {h.reload > 0 ? <b class="reloading">Reloading</b> : <b>{h.ammo}</b>}
            <span>/{h.maxAmmo}</span>
          </div>
        ) : (
          <div class="rv-ammo">
            <b class="inf">∞</b>
          </div>
        )}
        {h.maxAmmo > 0 && <AmmoTicks ammo={h.ammo} max={h.maxAmmo} reload={h.reload} />}
      </div>
      <div class="rv-abilities">
        {h.abilities.map((a) => (
          <AbilityTile key={a.slot} a={a} hero={h.hero} held={!!(h.buttons & (SLOT_BTN[a.slot] ?? 0))} />
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ bottom-centre: ultimate

function UltEmblem({ h }: { h: HudState }) {
  const ult = heroDef(h.hero, h.form).abilities[Slot.Ultimate];
  const pct = Math.floor(h.ult * 100);
  const ready = h.ult >= 1;
  const prev = useRef(ready);
  const [readyAt, setReadyAt] = useState(0);
  useEffect(() => {
    if (!prev.current && ready) setReadyAt(Date.now());
    prev.current = ready;
  }, [ready]);
  const R = 50;
  const circ = 2 * Math.PI * R;
  const seg = circ / 36;
  return (
    <div class={`rv-ult${ready ? " ready" : ""}${h.ultActive ? " active" : ""}`} aria-label={`${ult.name} ${ready ? "ready" : `${pct}%`}`}>
      {ready && (
        <svg class="rv-ult-burst" viewBox="-100 -100 200 200" aria-hidden="true">
          {Array.from({ length: 16 }, (_, i) => (
            <path d="M-5 -58 L0 -98 L5 -58 Z" transform={`rotate(${i * 22.5})`} />
          ))}
        </svg>
      )}
      <svg class="rv-ult-ring" viewBox="0 0 120 120" aria-hidden="true">
        <circle cx="60" cy="60" r={R} class="rv-ult-track" />
        <circle cx="60" cy="60" r={R} class="rv-ult-prog" stroke-dasharray={`${circ * Math.min(1, h.ult)} ${circ}`} transform="rotate(-90 60 60)" />
        <circle cx="60" cy="60" r={R} class="rv-ult-gaps" stroke-dasharray={`1.6 ${seg - 1.6}`} transform="rotate(-90 60 60)" />
        <circle cx="60" cy="60" r="41" class="rv-ult-core-bg" />
        <circle cx="60" cy="60" r="41" fill="url(#rv-ult-dots)" />
        <defs>
          <pattern id="rv-ult-dots" width="4" height="4" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.8" fill="#fff" opacity="0.07" />
          </pattern>
        </defs>
      </svg>
      <div class="rv-ult-core">
        <AbilityIcon name={ult.name} hero={h.hero} slot={Slot.Ultimate} size={34} />
        {h.ultActive ? <span class="rv-ult-pct">Active</span> : !ready && <span class="rv-ult-pct">{pct}%</span>}
      </div>
      <kbd class="rv-ult-key">Q</kbd>
      <span class="rv-ult-name">{ult.name}</span>
      {readyAt > 0 && Date.now() - readyAt < 2400 && (
        <div key={readyAt} class="rv-ult-banner" role="status">
          Ultimate ready
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ death

function DeathCard({ h }: { h: HudState }) {
  const longest = useRef(Math.max(1, h.respawn));
  if (h.respawn > longest.current) longest.current = h.respawn;
  const k = h.killedBy;
  return (
    <div class="rv-death" role="status">
      <div class="rv-death-kicker">{k ? "Defeated by" : "You were defeated"}</div>
      {k && (
        <div class="rv-death-killer" style={{ "--hc": HEROES[k.hero].color }}>
          <HeroPortrait hero={k.hero} />
          <div>
            <b>{k.name}</b>
            <span>{HEROES[k.hero].name}</span>
          </div>
        </div>
      )}
      {k && k.with.length > 0 && (
        <div class="rv-death-with">
          <span>with</span>
          {k.with.map((w) => (
            <span key={w.name} class="rv-death-with-chip">
              <HeroPortrait hero={w.hero} />
              {w.name}
            </span>
          ))}
        </div>
      )}
      <div class="rv-respawn">
        <span>{h.respawn > 0 ? "Respawn in" : "Respawning"}</span>
        <b>{h.respawn > 0 ? h.respawn : ""}</b>
        <i style={{ "--p": String(h.respawn / longest.current) }} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ overlays & chrome

function Vignettes({ low }: { low: boolean }) {
  const at = ui.hurt.value;
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!at) return;
    setOn(true);
    const t = setTimeout(() => setOn(false), 70);
    return () => clearTimeout(t);
  }, [at]);
  return (
    <>
      <div class={`rv-hurt${on ? " on" : ""}`} />
      {low && <div class="rv-lowhp" />}
    </>
  );
}

function Toast() {
  const t = ui.toast.value;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!t) return;
    if (Date.now() - t.at < 500) playUi(toastCue(t.error));
    const tm = setTimeout(() => tick((n) => n + 1), 3100);
    return () => clearTimeout(tm);
  }, [t?.at]);
  if (!t || Date.now() - t.at > 3000) return null;
  return (
    <div key={t.at} class={`rv-toast${t.error ? " error" : ""}`} role="alert">
      {t.text}
    </div>
  );
}

function NetGraph() {
  const n = ui.net.value;
  if (!n) return null;
  const row = (k: string, v: ComponentChildren) => (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
  return (
    <dl class="netgraph">
      {row("fps", ui.fps.value)}
      {row("link", ui.transport.value)}
      {row("rtt", `${n.rtt.toFixed(0)} ms`)}
      {row("snap", `${n.snapshotsPerSec.toFixed(0)}/s · ${((n.bytesPerSec * 8) / 1000).toFixed(0)} kbps`)}
      {row("loss", `${n.lossPct.toFixed(1)}%`)}
      {row("pred", `${n.pendingInputs} pending · ${n.corrections} corr (${(n.lastCorrection * 100).toFixed(1)} cm)`)}
      {row("queue", `${n.serverQueue.toFixed(1)} · ×${n.timeScale.toFixed(2)}`)}
    </dl>
  );
}
